import { describeError, uploadToPresignedUrl, useApi } from '@classgo/app-kit';
import type {
  ImportDryRunResult,
  ImportEntity,
  ImportJobView,
  ImportPlacements,
  ImportRowResult,
  ImportTemplate,
} from '@classgo/shared';
import { Badge, Button, Card, EmptyState, Input, Table, useToast } from '@classgo/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { type ChangeEvent, useState } from 'react';

import { useT } from '../../i18n';
import { downloadCsv, fileToBase64, importQueries, importsApi } from '../../imports/api';
import { PlacementPanel } from './placement';

type Step =
  | { kind: 'pick' }
  | { kind: 'file'; template: ImportTemplate }
  | {
      kind: 'review';
      template: ImportTemplate;
      file: File;
      content: string;
      result: ImportDryRunResult;
      placements: ImportPlacements;
    }
  | { kind: 'status'; template: ImportTemplate; jobId: string };

const MAX_ROWS_SHOWN = 500;
const NO_PLACEMENTS: ImportPlacements = { groups: [] };

/**
 * The import wizard (ticket #67): pick an entity, download its template, upload the filled
 * file, read the dry run with its Spanish row errors, confirm, follow the run, download the
 * error file and import again.
 */
export const ImportsScreen = () => {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const [step, setStep] = useState<Step>({ kind: 'pick' });
  const templates = useQuery(importQueries.templates(api));
  const pick = useMutation({
    mutationFn: (entity: ImportEntity) => importsApi(api).template(entity),
    onSuccess: (template) => setStep({ kind: 'file', template }),
  });
  const dryRun = useMutation({
    mutationFn: async (input: {
      template: ImportTemplate;
      file: File;
      content?: string;
      placements?: ImportPlacements;
    }) => {
      const { template, file } = input;
      const content = input.content ?? (await fileToBase64(file));
      const placements = input.placements ?? NO_PLACEMENTS;
      const result = await importsApi(api).dryRun({
        entity: template.entity,
        content,
        filename: file.name,
        ...(placements.groups.length > 0 || placements.schoolYearId ? { placements } : {}),
      });
      return { template, file, content, result, placements };
    },
    onSuccess: (data) => setStep({ kind: 'review', ...data }),
    onError: (error) =>
      toast.show({
        tone: 'error',
        title: t('imports.error.dryRun'),
        description: describeError(t, error),
      }),
  });
  const apply = useMutation({
    mutationFn: async ({
      template,
      file,
      placements,
    }: {
      template: ImportTemplate;
      file: File;
      placements: ImportPlacements;
    }) => {
      const presigned = await importsApi(api).presign(file.name, file.size);
      await uploadToPresignedUrl({ url: presigned.url, file, contentType: 'text/csv' });
      return importsApi(api).create({
        files: [
          {
            entity: template.entity,
            key: presigned.key,
            filename: file.name,
            ...(placements.groups.length > 0 || placements.schoolYearId ? { placements } : {}),
          },
        ],
      });
    },
    onSuccess: (job, { template }) => setStep({ kind: 'status', template, jobId: job.id }),
    onError: (error) =>
      toast.show({
        tone: 'error',
        title: t('imports.error.apply'),
        description: describeError(t, error),
      }),
  });

  const onFile = (template: ImportTemplate) => (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) dryRun.mutate({ template, file });
  };

  if (step.kind === 'pick') {
    return (
      <Card title={t('imports.title')}>
        <p className="mb-4 text-sm text-gray-600">{t('imports.intro')}</p>
        {templates.isError ? (
          <EmptyState
            title={t('entity.error.title')}
            description={describeError(t, templates.error)}
          />
        ) : templates.isPending ? (
          <p className="text-sm text-gray-500">{t('common.loading')}</p>
        ) : (
          <ol className="grid gap-3 sm:grid-cols-2">
            {templates.data.items.map((template) => (
              <li key={template.entity}>
                <button
                  type="button"
                  onClick={() => pick.mutate(template.entity)}
                  disabled={pick.isPending}
                  className="w-full rounded-card border border-gray-200 p-4 text-left hover:border-brand-400 hover:bg-brand-50"
                >
                  <p className="font-medium text-gray-900">
                    {template.order}. {template.title}
                  </p>
                  <p className="mt-1 text-sm text-gray-500">{template.description}</p>
                </button>
              </li>
            ))}
          </ol>
        )}
      </Card>
    );
  }

  if (step.kind === 'file') {
    const { template } = step;
    return (
      <Card title={t('imports.step.file', { entity: template.title })}>
        <p className="text-sm text-gray-600">{template.description}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => downloadCsv(template.filename, template.csv)}>
            {t('imports.downloadTemplate')}
          </Button>
          <Button
            variant="ghost"
            onClick={async () => {
              const filled = await importsApi(api).template(template.entity, 'tenant');
              downloadCsv(filled.filename, filled.csv);
            }}
          >
            {t('imports.downloadCurrent')}
          </Button>
          <Button variant="ghost" onClick={() => setStep({ kind: 'pick' })}>
            {t('imports.back')}
          </Button>
        </div>
        <details className="mt-4 text-sm">
          <summary className="cursor-pointer text-gray-700">{t('imports.columns')}</summary>
          <dl className="mt-2 grid gap-2 sm:grid-cols-2">
            {template.columns.map((column) => (
              <div key={column.key} className="rounded-control bg-gray-50 p-2">
                <dt className="font-mono text-xs text-gray-900">
                  {column.header}
                  {column.required ? <span className="text-danger-600"> *</span> : null}
                </dt>
                <dd className="text-gray-600">{column.description}</dd>
              </div>
            ))}
          </dl>
        </details>
        <div className="mt-6 max-w-md">
          <Input
            label={t('imports.file')}
            type="file"
            accept=".csv,text/csv,.txt"
            onChange={onFile(template)}
            disabled={dryRun.isPending}
            help={t('imports.file.help')}
          />
          {dryRun.isPending ? (
            <p className="mt-2 text-sm text-gray-500">{t('imports.validating')}</p>
          ) : null}
        </div>
      </Card>
    );
  }

  if (step.kind === 'review') {
    const { template, file, content, result, placements } = step;
    const rerun = (next: ImportPlacements) =>
      dryRun.mutate({ template, file, content, placements: next });
    const problems = result.rows.filter((row) => row.status === 'error' || row.warnings.length > 0);
    const shown = problems.slice(0, MAX_ROWS_SHOWN);
    const columns = [
      {
        key: 'line',
        header: t('imports.row.line'),
        cell: (row: ImportRowResult) => String(row.line),
        align: 'right' as const,
      },
      {
        key: 'status',
        header: t('imports.row.status'),
        cell: (row: ImportRowResult) => (
          <Badge tone={row.status === 'error' ? 'danger' : 'warning'}>
            {row.status === 'error' ? t('imports.row.error') : t('imports.row.warning')}
          </Badge>
        ),
      },
      {
        key: 'issues',
        header: t('imports.row.issues'),
        cell: (row: ImportRowResult) => (
          <ul className="space-y-1">
            {[...row.issues, ...row.warnings].map((issue, index) => (
              <li key={`${issue.column ?? ''}-${index}`}>
                {issue.column ? (
                  <span className="font-mono text-xs text-gray-500">{issue.column}: </span>
                ) : null}
                {issue.message}
              </li>
            ))}
          </ul>
        ),
      },
    ];
    return (
      <Card title={t('imports.step.review', { entity: template.title })}>
        <p className="text-sm text-gray-600">
          {t('imports.review.file', {
            name: file.name,
            encoding: result.encoding,
            separator: result.separator === '\t' ? 'TAB' : result.separator,
          })}
        </p>
        <dl className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-5">
          {(
            [
              ['rows', result.totals.rows],
              ['ok', result.totals.ok],
              ['errors', result.totals.errors],
              ['create', result.totals.create],
              ['update', result.totals.update],
            ] as const
          ).map(([key, value]) => (
            <div key={key} className="rounded-control bg-gray-50 p-3">
              <dt className="text-xs text-gray-500">{t(`imports.totals.${key}`)}</dt>
              <dd className="text-lg font-semibold text-gray-900">{value}</dd>
            </div>
          ))}
        </dl>
        {result.fileErrors.length > 0 ? (
          <ul
            role="alert"
            className="mt-3 list-disc rounded-control bg-danger-50 p-3 pl-8 text-sm text-danger-700"
          >
            {result.fileErrors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        ) : null}
        {result.placement ? (
          <PlacementPanel
            entity={template.entity}
            gaps={result.placement}
            placements={placements}
            busy={dryRun.isPending}
            onChange={rerun}
            onCreated={() => rerun(placements)}
          />
        ) : null}
        {result.columns.unknown.length > 0 ? (
          <p className="mt-2 text-sm text-gray-500">
            {t('imports.review.unknownColumns', { columns: result.columns.unknown.join(', ') })}
          </p>
        ) : null}
        <div className="mt-4">
          {problems.length === 0 ? (
            <p className="text-sm text-success-700">{t('imports.review.clean')}</p>
          ) : (
            <>
              <Table
                columns={columns}
                rows={shown}
                rowKey={(row) => String(row.line)}
                caption={t('imports.review.tableCaption')}
              />
              {problems.length > shown.length ? (
                <p className="mt-2 text-sm text-gray-500">
                  {t('imports.review.more', { count: problems.length - shown.length })}
                </p>
              ) : null}
            </>
          )}
        </div>
        <div className="mt-6 flex flex-wrap gap-2">
          <Button
            onClick={() => apply.mutate({ template, file, placements })}
            disabled={
              result.totals.ok === 0 ||
              result.fileErrors.length > 0 ||
              apply.isPending ||
              dryRun.isPending
            }
          >
            {t('imports.apply', { count: result.totals.ok })}
          </Button>
          <Button variant="secondary" onClick={() => setStep({ kind: 'file', template })}>
            {t('imports.anotherFile')}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <ImportStatus
      template={step.template}
      jobId={step.jobId}
      onRestart={() => setStep({ kind: 'pick' })}
    />
  );
};

const ImportStatus = ({
  template,
  jobId,
  onRestart,
}: {
  template: ImportTemplate;
  jobId: string;
  onRestart: () => void;
}) => {
  const t = useT();
  const api = useApi();
  const job = useQuery(importQueries.job(api, jobId));
  const statusTone = (status: ImportJobView['status']) =>
    status === 'done' ? 'success' : status === 'failed' ? 'danger' : 'info';
  return (
    <Card title={t('imports.step.status', { entity: template.title })}>
      {job.isError ? (
        <EmptyState title={t('entity.error.title')} description={describeError(t, job.error)} />
      ) : job.isPending ? (
        <p className="text-sm text-gray-500">{t('common.loading')}</p>
      ) : (
        <div className="space-y-4">
          <p className="flex items-center gap-2 text-sm text-gray-700">
            <Badge tone={statusTone(job.data.status)}>
              {t(`imports.status.${job.data.status}`)}
            </Badge>
            {job.data.status === 'queued' || job.data.status === 'running'
              ? t('imports.status.wait')
              : null}
          </p>
          {job.data.files.map((file) => (
            <div key={file.key} className="rounded-control bg-gray-50 p-3 text-sm">
              <p className="font-medium text-gray-900">{file.filename}</p>
              {file.counts ? (
                <p className="mt-1 text-gray-600">
                  {t('imports.counts', {
                    rows: file.counts.rows,
                    created: file.counts.created,
                    updated: file.counts.updated,
                    unchanged: file.counts.unchanged,
                    errors: file.counts.errors,
                  })}
                </p>
              ) : null}
              {file.fileErrors.length > 0 ? (
                <ul role="alert" className="mt-1 list-disc pl-5 text-danger-700">
                  {file.fileErrors.map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                </ul>
              ) : null}
              {file.errorUrl ? (
                <a
                  href={file.errorUrl}
                  className="mt-2 inline-block text-brand-600 underline underline-offset-2 hover:decoration-2"
                  download
                >
                  {t('imports.errorFile', { count: file.counts?.errors ?? 0 })}
                </a>
              ) : null}
            </div>
          ))}
          {job.data.error ? <p className="text-sm text-danger-700">{job.data.error}</p> : null}
          <Button variant="secondary" onClick={onRestart}>
            {t('imports.again')}
          </Button>
        </div>
      )}
    </Card>
  );
};
